/**
 * Shared, fail-safe PII masking for the operational surfaces
 * (Ops Inbox, Dispatch, Control Tower, AOC).
 *
 * A single privacy/overhead mode drives every masker so that when it is ON,
 * customer names, WhatsApp handles/message text, emails and phone numbers are
 * consistently hidden or masked across all surfaces — and when it is OFF the
 * raw values render exactly as before.
 *
 * The privacy mode is resolved from the same URL parameters the AOC control
 * tower already uses (`?privacy=on|off` and `display=overhead`) — see
 * `resolvePrivacyModeFromParams` / the `usePrivacyMode` hook — so there is one
 * plumbing, not a parallel one.
 *
 * These maskers are best-effort user-facing safety rails, not cryptographic
 * guarantees. Structured fields (name/email/phone/handle) keep a minimal,
 * non-identifying hint (a leading initial or trailing digits) for operator
 * continuity; free-text customer content (message bodies, summaries) is hidden
 * entirely because names can be embedded anywhere in prose.
 */

/** Bullet used for masked characters. */
const BULLET = '•••'; // •••

/** Default placeholder for hidden free-text customer content. */
export const HIDDEN_TEXT_PLACEHOLDER = 'Hidden in privacy mode';

/**
 * Resolve the shared privacy/overhead mode from URL search params.
 * Matches the AOC control-tower convention exactly:
 *   `display=overhead` OR `privacy=on` => privacy mode ON.
 */
export function resolvePrivacyModeFromParams(params: URLSearchParams): boolean {
  return params.get('display') === 'overhead' || params.get('privacy') === 'on';
}

/**
 * Session-scoped persistence for the privacy/overhead mode.
 *
 * The mode is activated by a URL param on ONE surface (e.g. an overhead
 * wallboard link), but drill-downs navigate to pages that never see that
 * param — so masking would silently drop one click later. Persisting the
 * active state in `sessionStorage` lets `usePrivacyMode` keep returning `true`
 * across navigation for the life of the tab, without threading params through
 * every call site (which is exactly what kept re-introducing PII leaks).
 *
 * sessionStorage (not localStorage) so the masked session ends when the tab
 * closes; wrapped in try/catch because storage can throw (private mode, SSR).
 */
const PRIVACY_MODE_SESSION_KEY = 'pt.privacyMode';

export function persistPrivacyMode(active: boolean): void {
  try {
    if (active) window.sessionStorage.setItem(PRIVACY_MODE_SESSION_KEY, '1');
    else window.sessionStorage.removeItem(PRIVACY_MODE_SESSION_KEY);
  } catch {
    /* sessionStorage unavailable — fail open to the URL/store-derived value */
  }
}

export function readPersistedPrivacyMode(): boolean {
  try {
    return window.sessionStorage.getItem(PRIVACY_MODE_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Mask a person's name, keeping only the leading initial of each token.
 * "Daniel Kruger" -> "D••• K•••". Falls back to a placeholder when empty.
 */
export function maskPersonName(name?: string | null, fallback = 'Name hidden'): string {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return fallback;
  return trimmed
    .split(/\s+/)
    .map((part) => {
      const initial = part.charAt(0);
      return initial ? `${initial}${BULLET}` : '';
    })
    .filter(Boolean)
    .join(' ');
}

/**
 * Mask an email address, keeping the first local-part character and the domain.
 * "danielk@propellertravel.com" -> "d•••@propellertravel.com".
 */
export function maskEmailAddress(email?: string | null, fallback = 'Email hidden'): string {
  const trimmed = (email ?? '').trim();
  if (!trimmed) return fallback;
  const at = trimmed.lastIndexOf('@');
  if (at <= 0 || at === trimmed.length - 1) {
    // Not a well-formed address — hide it entirely rather than leak it.
    return `${BULLET}`;
  }
  const local = trimmed.slice(0, at);
  const domain = trimmed.slice(at + 1);
  const head = local.charAt(0);
  return `${head}${BULLET}@${domain}`;
}

/**
 * Mask a phone number, keeping only the last two digits.
 * "+44 7911 123456" -> "•••56".
 */
export function maskPhoneNumber(phone?: string | null, fallback = 'Phone hidden'): string {
  const trimmed = (phone ?? '').trim();
  if (!trimmed) return fallback;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length < 2) return `${BULLET}`;
  return `${BULLET}${digits.slice(-2)}`;
}

/**
 * Mask a messaging handle (WhatsApp @handle, JID or phone-style handle).
 * "@danielbklon" -> "@d•••"; a phone-style handle masks like a phone number.
 */
export function maskHandle(handle?: string | null, fallback = 'Handle hidden'): string {
  const trimmed = (handle ?? '').trim();
  if (!trimmed) return fallback;
  if (trimmed.startsWith('@')) {
    const body = trimmed.slice(1);
    const head = body.charAt(0);
    return head ? `@${head}${BULLET}` : `@${BULLET}`;
  }
  // Phone-style / JID handle (mostly digits) -> mask like a phone number.
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length >= 4 && digits.length / trimmed.length > 0.5) {
    return maskPhoneNumber(trimmed, fallback);
  }
  const head = trimmed.charAt(0);
  return head ? `${head}${BULLET}` : `${BULLET}`;
}

/**
 * Hide free-text customer content (message bodies, work-item summaries,
 * reconciliation observations) entirely. Names, numbers and handles can be
 * embedded anywhere in prose, so the fail-safe is to not render it at all.
 * Returns the placeholder when there was content, '' when there was none.
 */
export function maskFreeText(
  text?: string | null,
  placeholder = HIDDEN_TEXT_PLACEHOLDER,
): string {
  const trimmed = (text ?? '').trim();
  if (!trimmed) return '';
  return placeholder;
}

/**
 * Mask a booking reference (record locator), keeping only its first two characters.
 * "AB12CD" -> "AB****". Staff screens show the full reference; this is for privacy mode only.
 */
export function maskBookingReference(ref?: string | null, fallback = 'Reference hidden'): string {
  const trimmed = (ref ?? '').trim();
  if (!trimmed) return fallback;
  return `${trimmed.slice(0, 2)}****`;
}

/**
 * Replace every occurrence of the given booking references inside a sentence by their masked form
 * (privacy mode only). References shorter than four characters are ignored, so a stray "AB" is never
 * treated as a reference.
 */
export function maskBookingReferencesIn(text: string, refs: ReadonlyArray<string | null | undefined>): string {
  let out = text;
  for (const ref of refs) {
    const value = (ref ?? '').trim();
    if (value.length >= 4) out = out.split(value).join(maskBookingReference(value));
  }
  return out;
}

/** Placeholder printed in place of a money amount in privacy mode. */
export const HIDDEN_AMOUNT_PLACEHOLDER = '••••';

/**
 * Hide a money amount (privacy mode only). Never echoes any digit of the amount, nor the currency, so a
 * shared screen cannot be used to read a total, a margin or a payroll figure. Returns '' when there was
 * no amount at all (so an empty cell stays empty), the placeholder otherwise.
 */
export function maskAmount(
  amount?: number | string | null,
  placeholder = HIDDEN_AMOUNT_PLACEHOLDER,
): string {
  if (amount === null || amount === undefined) return '';
  if (typeof amount === 'string' && amount.trim() === '') return '';
  return placeholder;
}

/** Placeholder printed in place of an access token (share link, secret link) in privacy mode. */
export const HIDDEN_TOKEN_PLACEHOLDER = 'Link hidden';

/**
 * Mask an access token or an access link. Only the placeholder is returned: even a prefix of a bearer token
 * shortens an attacker's search, so no character is kept. Empty input stays empty.
 */
export function maskAccessToken(
  token?: string | null,
  placeholder = HIDDEN_TOKEN_PLACEHOLDER,
): string {
  return (token ?? '').trim() ? placeholder : '';
}

/**
 * Mask an opaque identifier (a customer number, a ticket number, an id shown to staff), keeping only its last
 * two characters. "1002472" -> "•••72".
 */
export function maskIdentifier(id?: string | number | null, fallback = 'ID hidden'): string {
  const trimmed = String(id ?? '').trim();
  if (!trimmed) return fallback;
  if (trimmed.length <= 2) return BULLET;
  return `${BULLET}${trimmed.slice(-2)}`;
}
